import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Paper,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Menu,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api"; // <-- Centralized API client
import { buildItemUrl } from "../utils/itemUrl";
import { formatPrice, getPriceDollars } from "../utils/price";
import PaginationControls from "./PaginationControls";
import usePageTitle from "../hooks/usePageTitle";
import { toDisplayImageUrl } from "../utils/imageUrl";
import { MODERATION_REJECTION_MESSAGE, moderateTextFields } from "../utils/textModeration";
import { cachePreferenceResponse, fetchPreferencesOnce, usePriceVisibility } from "../contexts/PriceVisibilityContext";

// read user from localStorage OR sessionStorage
const readStoredUser = () => {
  try {
    const raw = localStorage.getItem("user") || sessionStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const ALLOWED_ITEMS_PER_PAGE = [12, 24, 36];
const ALLOWED_SORT_OPTIONS = ["high", "low", "az", "za", "newest", "oldest"];
const getStoredSortPreference = () => { const shared = localStorage.getItem("collection_sort_preference"); const legacy = localStorage.getItem("manage_sort_preference"); const value = ALLOWED_SORT_OPTIONS.includes(shared) ? shared : ALLOWED_SORT_OPTIONS.includes(legacy) ? legacy : "high"; if (value !== shared) localStorage.setItem("collection_sort_preference", value); return value; };
const normalizeItemsPerPagePreference = (value) => {
  const parsed = Number(value);
  return ALLOWED_ITEMS_PER_PAGE.includes(parsed) ? parsed : 12;
};
const normalizeSubcategoryId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};
const buildSubcategoryTree = (items = []) => {
  const nodes = items
    .map((item) => ({
      ...item,
      subcategory_id: normalizeSubcategoryId(item.subcategory_id),
      parent_subcategory_id: normalizeSubcategoryId(item.parent_subcategory_id),
      children: [],
    }))
    .filter((item) => item.subcategory_id != null);
  const byId = new Map(nodes.map((node) => [node.subcategory_id, node]));
  const roots = [];

  nodes.forEach((node) => {
    const parentId = node.parent_subcategory_id;
    if (parentId != null && byId.has(parentId)) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (list) => {
    list.sort((a, b) => {
      const parentA = a.name || "";
      const parentB = b.name || "";
      return parentA.localeCompare(parentB);
    });
    list.forEach((node) => {
      if (node.children.length > 0) {
        sortNodes(node.children);
      }
    });
  };

  sortNodes(roots);
  return roots;
};
const getSubcategoryScopeIds = (items, selectedValue) => {
  const selectedId = normalizeSubcategoryId(selectedValue);
  if (selectedId == null) return null;
  const byId = new Map(
    items
      .map((item) => ({
        ...item,
        subcategory_id: normalizeSubcategoryId(item.subcategory_id),
        parent_subcategory_id: normalizeSubcategoryId(item.parent_subcategory_id),
      }))
      .filter((item) => item.subcategory_id != null)
      .map((item) => [item.subcategory_id, item])
  );
  const selected = byId.get(selectedId);
  if (!selected) return null;
  if (selected.parent_subcategory_id != null) {
    return [selectedId];
  }
  const childIds = items
    .map((item) => ({
      ...item,
      subcategory_id: normalizeSubcategoryId(item.subcategory_id),
      parent_subcategory_id: normalizeSubcategoryId(item.parent_subcategory_id),
    }))
    .filter(
      (item) =>
        item.subcategory_id != null &&
        item.parent_subcategory_id === selectedId
    )
    .map((item) => item.subcategory_id);
  return [selectedId, ...childIds];
};
const getSubcategoryDisplayPath = (item = {}) => {
  const parent = item.parent_subcategory_name;
  const child = item.subcategory_name;
  if (parent && child && parent !== child) return `${parent} > ${child}`;
  return child || parent || "";
};
const renderSubcategoryChips = (item = {}) => {
  const parent = item.parent_subcategory_name;
  const child = item.subcategory_name;

  const chips = [];

  if (parent) {
    chips.push(
      <Chip
        key="parent"
        label={parent}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 600,
          height: 24,
          borderRadius: "999px",
          bgcolor: "#fff",
          color: "#111",
          border: "1px solid rgba(255,255,255,0.88)",
          borderColor: "rgba(255,255,255,0.88)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
        }}
      />
    );
  }

  if (child && child !== parent) {
    chips.push(
      <Chip
        key="child"
        label={child}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 600,
          height: 24,
          borderRadius: "999px",
          bgcolor: "#fff",
          color: "#111",
          border: "1px solid rgba(255,255,255,0.88)",
          borderColor: "rgba(255,255,255,0.88)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.75,
        flexWrap: "wrap",
        mt: 0.5,
      }}
    >
      {chips}
    </Box>
  );
};
const getStoredItemsPerPagePreference = () => {
  const storedUser = readStoredUser();
  if (storedUser && storedUser.items_per_page_preference != null) {
    const userPref = normalizeItemsPerPagePreference(storedUser.items_per_page_preference);
    if (ALLOWED_ITEMS_PER_PAGE.includes(userPref)) return userPref;
  }
  const raw = localStorage.getItem("items_per_page_preference");
  const localPref = normalizeItemsPerPagePreference(raw);
  return ALLOWED_ITEMS_PER_PAGE.includes(localPref) ? localPref : 12;
};

const Manage = () => {
  usePageTitle("Manage Collection");
  const navigate = useNavigate();
  const { username: routeUsername } = useParams();
  const { showPrices: pricePreferenceEnabled, pricesLoaded, setShowPrices } = usePriceVisibility();
  const showPrices = pricesLoaded && pricePreferenceEnabled;

  const [viewUsername, setViewUsername] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [profileUser, setProfileUser] = useState(null);

  const [collection, setCollection] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const [selectedUserItemIds, setSelectedUserItemIds] = useState([]);
  const [filterSubcategory, setFilterSubcategory] = useState("all");
  const [assignSubcategory, setAssignSubcategory] = useState("");
  const [editPrices, setEditPrices] = useState({});

  const [editingSubId, setEditingSubId] = useState(null);
  const [editingSubName, setEditingSubName] = useState("");
  const [editingSubParentId, setEditingSubParentId] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [sortItemOption, setSortItemOption] = useState(() => getStoredSortPreference());
  const [newSubcategoryName, setNewSubcategoryName] = useState("");
  const [newSubcategoryParentId, setNewSubcategoryParentId] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [error, setError] = useState("");
  const [profilePicInput, setProfilePicInput] = useState("");
  const [savingProfilePic, setSavingProfilePic] = useState(false);
  const [preferenceMessage, setPreferenceMessage] = useState("");
  const [preferenceError, setPreferenceError] = useState("");
  const [savingPreference, setSavingPreference] = useState(false);
  const [privacyPreferences, setPrivacyPreferences] = useState(null);
  const [privacySaving, setPrivacySaving] = useState("");
  const [privacyMessage, setPrivacyMessage] = useState("");
  const [privacyError, setPrivacyError] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [organizeAnchor, setOrganizeAnchor] = useState(null);
  const [organizeItem, setOrganizeItem] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [subcategoryPage, setSubcategoryPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => getStoredItemsPerPagePreference());
  const subcategoriesPerPage = 5;
  const solidButtonSx = { fontWeight: 700, height: 40 };
  const solidButtonCompactSx = { fontWeight: 700, height: 40, minWidth: 88 };
  const subcategoryTree = useMemo(() => buildSubcategoryTree(subcategories), [subcategories]);
  const topLevelSubcategories = useMemo(
    () => subcategoryTree.filter((sub) => normalizeSubcategoryId(sub.parent_subcategory_id) == null),
    [subcategoryTree]
  );
  const subcategoryScopeIds = useMemo(
    () => getSubcategoryScopeIds(subcategories, filterSubcategory),
    [subcategories, filterSubcategory]
  );
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);
  useEffect(() => { if (!showPrices && ["high", "low"].includes(sortItemOption)) setSortItemOption("az"); }, [showPrices, sortItemOption]);

  // resolve user
  useEffect(() => {
    const stored = readStoredUser();
    setCurrentUser(stored);

    if (routeUsername && routeUsername.trim()) {
      setViewUsername(routeUsername.trim());
      return;
    }
    const storedUsername = stored?.username || stored?.name || "";
    if (storedUsername) {
      navigate(`/manage/${encodeURIComponent(storedUsername)}`, {
        replace: true,
      });
    } else {
      setViewUsername("");
    }
  }, [routeUsername, navigate]);

  // hydrate logged-in user's record (profile pic, etc.)
  const hydratedOnce = useRef(false);
  useEffect(() => {
    if (hydratedOnce.current) return;
    if (!currentUser) return;
    const id = currentUser.id ?? currentUser.user_id;
    if (!id) return;

    hydratedOnce.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/api/users/${id}`);
        const merged = { ...currentUser, ...data };
        setCurrentUser(merged);
        localStorage.setItem("user", JSON.stringify(merged));
        sessionStorage.setItem("user", JSON.stringify(merged));
        window.dispatchEvent(new Event("storage"));
      } catch {
        // ignore
      }
    })();
  }, [currentUser]);

  // derive profile to show (only if viewing own manage page)
  useEffect(() => {
    const match =
      currentUser?.username &&
      viewUsername &&
      currentUser.username.toLowerCase() === viewUsername.toLowerCase();
    const val = match ? currentUser : null;
    setProfileUser(val);
    if (val?.profile_picture) {
      setProfilePicInput(val.profile_picture);
    }
  }, [currentUser, viewUsername]);

  useEffect(() => {
    const pref = normalizeItemsPerPagePreference(currentUser?.items_per_page_preference);
    if (ALLOWED_ITEMS_PER_PAGE.includes(pref) && pref !== itemsPerPage) {
      setItemsPerPage(pref);
    }
  }, [currentUser, itemsPerPage]);

  const authenticatedManageUserId = currentUser?.id ?? currentUser?.user_id ?? null;
  useEffect(() => {
    if (!authenticatedManageUserId) return;
    let cancelled = false;
    setPrivacyError("");
    fetchPreferencesOnce(authenticatedManageUserId).then(({ data }) => {
      if (!cancelled) setPrivacyPreferences({ is_private: Boolean(data.is_private), show_collection_prices: Boolean(data.show_collection_prices), show_prices_sitewide: data.show_prices_sitewide !== false });
    }).catch(() => { if (!cancelled) setPrivacyError("Privacy preferences could not be loaded."); });
    return () => { cancelled = true; };
  }, [authenticatedManageUserId]);

  const handlePrivacyToggle = async (field, checked) => {
    if (!privacyPreferences || privacySaving) return;
    const previous = privacyPreferences;
    setPrivacyPreferences({ ...previous, [field]: checked });
    if (field === "show_prices_sitewide") setShowPrices(checked);
    setPrivacySaving(field);
    setPrivacyMessage("");
    setPrivacyError("");
    try {
      const { data } = await api.patch("/api/users/me/preferences", { [field]: checked });
      if (authenticatedManageUserId) cachePreferenceResponse(authenticatedManageUserId, data);
      setPrivacyPreferences({ is_private: Boolean(data.is_private), show_collection_prices: Boolean(data.show_collection_prices), show_prices_sitewide: data.show_prices_sitewide !== false });
      if (field === "show_prices_sitewide") setShowPrices(data.show_prices_sitewide !== false);
      setPrivacyMessage("Privacy preference saved.");
    } catch {
      setPrivacyPreferences(previous);
      if (field === "show_prices_sitewide") setShowPrices(previous.show_prices_sitewide);
      setPrivacyError("Could not save that preference. Your previous setting was restored.");
    } finally { setPrivacySaving(""); }
  };

  // fetch helpers
  const fetchCollection = async (username) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(
        `/api/mycollection/${encodeURIComponent(username)}`
      );
      const rawItems = Array.isArray(data) ? data : data?.items || [];
      const items = rawItems.map((item) => ({
        ...item,
        price: getPriceDollars(item) ?? 0,
        subcategory_id:
          item.subcategory_id != null ? Number(item.subcategory_id) : null,
      }));
      setCollection(items);
    } catch (err) {
      console.error("❌ fetchCollection:", err);
      setError("Failed to load your collection.");
      setCollection([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcategories = async (username) => {
    setLoadingSubs(true);
    try {
      const { data } = await api.get(
        `/api/subcategories/user/${encodeURIComponent(username)}`
      );
      const list = Array.isArray(data) ? data : [];
      setSubcategories(
        list.map((s) => ({
          ...s,
          subcategory_id: normalizeSubcategoryId(s.subcategory_id),
          parent_subcategory_id: normalizeSubcategoryId(s.parent_subcategory_id),
        }))
      );
    } catch (err) {
      console.error("❌ fetchSubcategories:", err);
      setSubcategories([]);
    } finally {
      setLoadingSubs(false);
    }
  };

  useEffect(() => {
    if (!viewUsername) return;
    fetchCollection(viewUsername);
    fetchSubcategories(viewUsername);
    setSelectedUserItemIds([]);
    setFilterSubcategory("all");
    setAssignSubcategory("");
    setEditPrices({});
    setEditingSubId(null);
    setEditingSubName("");
    setEditingSubParentId("");
    setNewSubcategoryParentId("");
    setCurrentPage(1);
    setSubcategoryPage(1);
  }, [viewUsername]);

  // selection helpers
  const toggleSelect = (user_item_id) => {
    setSelectedUserItemIds((prev) =>
      prev.includes(user_item_id)
        ? prev.filter((id) => id !== user_item_id)
        : [...prev, user_item_id]
    );
  };

  const allSelectedOnPage = (pageItems) =>
    pageItems.length > 0 &&
    pageItems.every((it) => selectedUserItemIds.includes(it.user_item_id));

  const toggleSelectAllOnPage = (pageItems) => {
    const allSelected = allSelectedOnPage(pageItems);
    if (allSelected) {
      setSelectedUserItemIds((prev) =>
        prev.filter((id) => !pageItems.some((it) => it.user_item_id === id))
      );
    } else {
      const addIds = pageItems.map((it) => it.user_item_id);
      setSelectedUserItemIds((prev) => Array.from(new Set([...prev, ...addIds])));
    }
  };

  // ---- Subcategory CRUD handlers ----------------------------------------

  const handleCreateSubcategory = async () => {
    if (!viewUsername || !newSubcategoryName.trim()) return;
    const moderationResult = moderateTextFields({ name: newSubcategoryName });
    if (!moderationResult.ok) {
      // Frontend moderation: reject inappropriate subcategory names.
      alert(MODERATION_REJECTION_MESSAGE);
      return;
    }
    const parentId = normalizeSubcategoryId(newSubcategoryParentId);
    try {
      await api.post(
        `/api/subcategories/user/${encodeURIComponent(viewUsername)}`,
        {
          name: newSubcategoryName.trim(),
          parent_subcategory_id: parentId,
        }
      );
      setNewSubcategoryName("");
      setNewSubcategoryParentId("");
      setSubcategoryPage(1);
      await fetchSubcategories(viewUsername);
    } catch (err) {
      console.error("❌ create subcategory:", err);
      alert("Failed to create subcategory.");
    }
  };

  const startEditSubcategory = (sub) => {
    setEditingSubId(sub.subcategory_id);
    setEditingSubName(sub.name);
    setEditingSubParentId(
      normalizeSubcategoryId(sub.parent_subcategory_id) != null
        ? String(sub.parent_subcategory_id)
        : ""
    );
  };

  const handleSaveSubcategoryRename = async () => {
    if (!editingSubId || !editingSubName.trim()) {
      setEditingSubId(null);
      setEditingSubName("");
      return;
    }
    const moderationResult = moderateTextFields({ name: editingSubName });
    if (!moderationResult.ok) {
      alert(MODERATION_REJECTION_MESSAGE);
      return;
    }
    try {
      await api.patch(`/api/subcategories/${editingSubId}`, {
        name: editingSubName.trim(),
        parent_subcategory_id: normalizeSubcategoryId(editingSubParentId),
      });
      setEditingSubId(null);
      setEditingSubName("");
      setEditingSubParentId("");
      setSubcategoryPage(1);
      await fetchSubcategories(viewUsername);
    } catch (err) {
      console.error("❌ rename subcategory:", err);
      alert("Failed to rename subcategory.");
    }
  };

  const handleDeleteSubcategory = async (subId) => {
    if (
      !window.confirm(
        "Delete this subcategory? Items will keep their data, but lose this tag."
      )
    ) {
      return;
    }
    try {
      await api.delete(`/api/subcategories/${subId}`);
      setSubcategoryPage(1);
      await fetchSubcategories(viewUsername);
      await fetchCollection(viewUsername);
    } catch (err) {
      console.error("❌ delete subcategory:", err);
      alert("Failed to delete subcategory.");
    }
  };

  // actions on items
  const handleAssignSubcategory = async () => {
    if (!viewUsername || !assignSubcategory || selectedUserItemIds.length === 0)
      return;

    const itemIds = selectedUserItemIds
      .map((uid) => collection.find((it) => it.user_item_id === uid)?.item_id)
      .filter((v) => v != null);

    if (itemIds.length === 0) {
      alert("No items resolved for assignment.");
      return;
    }

    try {
      // NEW: use /api/subcategories/assign instead of /api/mycollection/.../subcategory/...
      await Promise.all(
        itemIds.map((itemId) =>
          api.post("/api/subcategories/assign", {
            username: viewUsername,
            itemId,
            subcategoryId: assignSubcategory,
          })
        )
      );

      await fetchCollection(viewUsername);
      setSelectedUserItemIds([]);
      setAssignSubcategory("");
      alert("📂 Items assigned to subcategory!");
    } catch (err) {
      console.error("❌ assign subcategory:", err);
      alert("Failed to assign subcategory.");
    }
  };

  const handleSavePrices = async () => {
    const userId = currentUser?.id ?? currentUser?.user_id;
    if (!userId) {
      alert("Please log in again.");
      return;
    }
    const updates = Object.entries(editPrices)
      .map(([userItemId, price]) => {
        const parsed = parseFloat(price);
        if (Number.isNaN(parsed)) return null;
        return api.patch(`/api/user_items/${userId}/${userItemId}/purchase_price`, {
          purchase_price: parsed,
        });
      })
      .filter(Boolean);

    try {
      if (updates.length > 0) await Promise.all(updates);
      await fetchCollection(viewUsername);
      alert("💰 Purchase prices updated!");
    } catch (err) {
      console.error("❌ save prices:", err);
      alert("Failed to save prices.");
    }
  };

  const handleDelete = async () => {
    const userId = currentUser?.id ?? currentUser?.user_id;
    if (!userId || selectedUserItemIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedUserItemIds.length} selected item(s)?`))
      return;

    try {
      await api.delete(`/api/user_items/${userId}/${selectedUserItemIds.join(",")}`);
      await fetchCollection(viewUsername);
      setSelectedUserItemIds([]);
      alert("🗑️ Items deleted!");
    } catch (err) {
      console.error("❌ delete items:", err);
      alert("Failed to delete items.");
    }
  };

  // filtering/sorting/paging
  const filtered = useMemo(() => {
    const normalizedQuery = (searchQuery || "").trim().toLowerCase();

    return collection.filter((item) => {
      const searchableText = [
        item.name,
        item.brand,
        item.category,
        item.subcategory_name,
        item.parent_subcategory_name,
        item.player,
        item.set_name,
        item.edition_variant,
        item.number,
        item.year_released,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const searchMatch =
        !normalizedQuery || searchableText.includes(normalizedQuery);
      const subMatch =
        subcategoryScopeIds === null ||
        subcategoryScopeIds.includes(Number(item.subcategory_id));

      return searchMatch && subMatch;
    });
  }, [collection, searchQuery, subcategoryScopeIds]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    switch (sortItemOption) {
      case "high":
        list.sort(
          (a, b) => (Number(b.price ?? 0) || 0) - (Number(a.price ?? 0) || 0)
        );
        break;
      case "low":
        list.sort(
          (a, b) => (Number(a.price ?? 0) || 0) - (Number(b.price ?? 0) || 0)
        );
        break;
      case "az":
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        break;
      case "za":
        list.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
        break;
      case "newest":
        list.sort(
          (a, b) =>
            new Date(b.added_on ?? b.created_at ?? 0) -
            new Date(a.added_on ?? a.created_at ?? 0)
        );
        break;
      case "oldest":
        list.sort(
          (a, b) =>
            new Date(a.added_on ?? a.created_at ?? 0) -
            new Date(b.added_on ?? b.created_at ?? 0)
        );
        break;
      default:
        break;
    }
    return list;
  }, [filtered, sortItemOption]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(sorted.length / itemsPerPage));
    if (currentPage > maxPage) {
      setCurrentPage(1);
    }
  }, [sorted.length, currentPage, itemsPerPage]);

  useEffect(() => {
    setSubcategoryPage(1);
  }, [subcategories]);

  useEffect(() => {
    const maxSubcategoryPage = Math.max(
      1,
      Math.ceil(subcategoryTree.length / subcategoriesPerPage)
    );
    if (subcategoryPage > maxSubcategoryPage) {
      setSubcategoryPage(1);
    }
  }, [subcategoryTree.length, subcategoryPage, subcategoriesPerPage]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / itemsPerPage));
  const pageItems = sorted.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const subcategoryTotalPages = Math.max(
    1,
    Math.ceil(subcategoryTree.length / subcategoriesPerPage)
  );
  const visibleSubcategories = subcategoryTree.slice(
    (subcategoryPage - 1) * subcategoriesPerPage,
    subcategoryPage * subcategoriesPerPage
  );

  const totalValue = useMemo(
    () =>
      sorted
        .reduce((sum, it) => sum + (Number(it.price ?? 0) || 0), 0),
    [sorted]
  );

  const hasPriceEdits = Object.keys(editPrices).length > 0;

  const handleSaveProfilePic = async () => {
    const userId = currentUser?.id ?? currentUser?.user_id;
    if (!userId) return;
    setSavingProfilePic(true);
    try {
      const url = profilePicInput.trim() || null;
      // try /api first, then fallback to /users if needed
      let resp;
      try {
        resp = await api.patch(
          `/api/users/${userId}/profile-picture`,
          { profile_picture: url }
        );
      } catch (err) {
        if (err?.response?.status === 404) {
          resp = await api.patch(
            `/users/${userId}/profile-picture`,
            { profile_picture: url }
          );
        } else {
          throw err;
        }
      }
      const data = resp?.data || {};
      const merged = {
        ...currentUser,
        profile_picture: data?.profile_picture ?? url,
      };
      setCurrentUser(merged);
      localStorage.setItem("user", JSON.stringify(merged));
      sessionStorage.setItem("user", JSON.stringify(merged));
      window.dispatchEvent(new Event("storage"));
    } catch (err) {
      console.error("❌ update profile picture:", err);
      alert("Failed to update profile picture.");
    } finally {
      setSavingProfilePic(false);
    }
  };

  const manageStatus = async (item, status, enabled) => {
    if (!item?.user_item_id) return;
    if (enabled && status === "locked" && (Number(item.is_for_trade) === 1 || Number(item.is_for_sale) === 1) && !window.confirm("Locking this item will remove it from For Trade and For Sale.")) return;
    setStatusBusy(true);
    try { await api.patch("/api/mycollection/status", { user_item_id: item.user_item_id, status, enabled }); await fetchCollection(viewUsername); setOrganizeAnchor(null); setOrganizeItem(null); }
    catch (err) { alert(err.response?.data?.error === "item_locked" ? `This item is Locked. Unlock it before marking it ${status === "for_trade" ? "For Trade" : "For Sale"}.` : "Unable to update item status."); }
    finally { setStatusBusy(false); }
  };

  const handleItemsPerPagePreferenceChange = async (nextValue) => {
    const normalized = normalizeItemsPerPagePreference(nextValue);
    setItemsPerPage(normalized);
    setCurrentPage(1);
    setSavingPreference(true);
    setPreferenceMessage("");
    setPreferenceError("");

    localStorage.setItem("items_per_page_preference", String(normalized));
    sessionStorage.setItem("items_per_page_preference", String(normalized));

    const userId = currentUser?.id ?? currentUser?.user_id;
    const mergedUser = currentUser
      ? { ...currentUser, items_per_page_preference: normalized }
      : null;
    if (mergedUser) {
      setCurrentUser(mergedUser);
      localStorage.setItem("user", JSON.stringify(mergedUser));
      sessionStorage.setItem("user", JSON.stringify(mergedUser));
      window.dispatchEvent(new Event("storage"));
    }

    if (!userId) {
      setPreferenceMessage("Display preference saved locally.");
      setSavingPreference(false);
      return;
    }

    try {
      const { data } = await api.patch(`/api/users/${userId}/preferences`, {
        items_per_page_preference: normalized,
      });
      const savedValue = normalizeItemsPerPagePreference(data?.items_per_page_preference);
      const savedUser = mergedUser
        ? { ...mergedUser, items_per_page_preference: savedValue }
        : { ...(currentUser || {}), items_per_page_preference: savedValue };
      setCurrentUser(savedUser);
      localStorage.setItem("user", JSON.stringify(savedUser));
      sessionStorage.setItem("user", JSON.stringify(savedUser));
      localStorage.setItem("items_per_page_preference", String(savedValue));
      sessionStorage.setItem("items_per_page_preference", String(savedValue));
      setItemsPerPage(savedValue);
      setPreferenceMessage("Display preference saved.");
    } catch (err) {
      console.error("Failed to save items per page preference:", err);
      setPreferenceError("Failed to save display preference.");
    } finally {
      setSavingPreference(false);
    }
  };

  const handleSortPreferenceChange = (nextValue) => {
    const normalized = ALLOWED_SORT_OPTIONS.includes(nextValue) ? nextValue : "high";
    setSortItemOption(normalized);
    setCurrentPage(1);
    localStorage.setItem("manage_sort_preference", normalized);
    sessionStorage.setItem("manage_sort_preference", normalized);
    localStorage.setItem("collection_sort_preference", normalized);
    sessionStorage.setItem("collection_sort_preference", normalized);
  };

  const renderSubcategoryMenuItems = (nodes = [], depth = 0) =>
    nodes.flatMap((sub) => [
      <MenuItem key={sub.subcategory_id} value={String(sub.subcategory_id)}>
        <Box sx={{ pl: depth * 2 }}>{sub.name}</Box>
      </MenuItem>,
      ...(sub.children?.length
        ? renderSubcategoryMenuItems(sub.children, depth + 1)
        : []),
    ]);

  const renderSubcategoryRows = (nodes = [], depth = 0) =>
    nodes.flatMap((sub) => [
      <Box
        key={sub.subcategory_id}
        sx={{
          pl: depth * 2,
          pr: 1,
          py: 1,
          borderLeft: depth > 0 ? "3px solid" : "none",
          borderColor: depth > 0 ? "primary.light" : "transparent",
          bgcolor: depth > 0 ? "action.hover" : "transparent",
          borderRadius: 1,
          ml: depth > 0 ? 1 : 0,
          mb: 1.25,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1, sm: 2 },
            width: "100%",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, pr: 1, whiteSpace: "normal", wordBreak: "break-word" }}>
            {editingSubId === sub.subcategory_id ? (
              <TextField
                size="small"
                fullWidth
                value={editingSubName}
                onChange={(e) => setEditingSubName(e.target.value)}
                sx={{ mb: 1 }}
              />
            ) : (
              <ListItemText
                primary={depth > 0 ? `↳ ${sub.name}` : sub.name}
                secondary={
                  depth > 0
                    ? "Child subcategory • filters only items assigned here"
                    : "Main subcategory • includes items from child subcategories when filtered"
                }
                primaryTypographyProps={{
                  fontWeight: depth > 0 ? 600 : 700,
                  variant: depth > 0 ? "body2" : "subtitle2",
                }}
                secondaryTypographyProps={{
                  sx: { color: "text.secondary", fontSize: 12, lineHeight: 1.35 },
                }}
              />
            )}
            {editingSubId === sub.subcategory_id && (
              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                <InputLabel>Place under</InputLabel>
                <Select
                  value={editingSubParentId}
                  label="Place under"
                  onChange={(e) => setEditingSubParentId(String(e.target.value))}
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value="">Top-level subcategory</MenuItem>
                  {topLevelSubcategories
                    .filter((parent) => parent.subcategory_id !== sub.subcategory_id)
                    .map((parent) => (
                      <MenuItem key={parent.subcategory_id} value={String(parent.subcategory_id)}>
                        {parent.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", sm: "row" },
              flexWrap: "wrap",
              gap: 1.5,
              flexShrink: 0,
              alignItems: "center",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {editingSubId === sub.subcategory_id ? (
              <>
                <Button
                  variant="contained"
                  disableElevation
                  size="small"
                  onClick={handleSaveSubcategoryRename}
                  sx={{ ...solidButtonCompactSx, minWidth: 72 }}
                >
                  Save
                </Button>

                <Button
                  variant="contained"
                  disableElevation
                  size="small"
                  color="secondary"
                  onClick={() => {
                    setEditingSubId(null);
                    setEditingSubName("");
                    setEditingSubParentId("");
                  }}
                  sx={{ ...solidButtonCompactSx, minWidth: 72 }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  disableElevation
                  size="small"
                  onClick={() => startEditSubcategory(sub)}
                  sx={{ ...solidButtonCompactSx, minWidth: 72 }}
                >
                  Edit
                </Button>

                <Button
                  variant="contained"
                  disableElevation
                  size="small"
                  color="error"
                  onClick={() => handleDeleteSubcategory(sub.subcategory_id)}
                  sx={{ ...solidButtonCompactSx, minWidth: 72 }}
                >
                  Delete
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>,
      ...(sub.children?.length ? renderSubcategoryRows(sub.children, depth + 1) : []),
    ]);

  if (!routeUsername && !currentUser?.username) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          Please log in to manage your collection.
        </Typography>
        <Button variant="contained" disableElevation sx={solidButtonSx} onClick={() => navigate("/login")}>
          Go to Login
        </Button>
      </Box>
    );
  }

  if (loading && collection.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography>Loading your collection…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: "auto" }}>
      {/* Header / summary */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            src={
              profileUser?.profile_picture
                ? profileUser.profile_picture
                : "/logos/default-avatar.jpg"
            }
            sx={{ width: 72, height: 72 }}
            imgProps={{
              referrerPolicy: "no-referrer",
              onError: (e) => {
                e.currentTarget.src = "/logos/default-avatar.jpg";
              },
            }}
          />
          <Box>
            <Typography variant="h6">@{viewUsername}</Typography>
            <Typography variant="body2">
              Total Items: <strong>{collection.length}</strong>
            </Typography>
            {showPrices && <Typography variant="body2">
              Total Value: <strong>{formatPrice({ price: totalValue.toFixed(2) }) || "TBD"}</strong>
            </Typography>}
            {profileUser && (
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                  alignItems: { xs: "stretch", sm: "center" },
                  width: "100%",
                }}
              >
                <TextField
                  label="Profile picture URL"
                  size="small"
                  value={profilePicInput}
                  onChange={(e) => setProfilePicInput(e.target.value)}
                  fullWidth
                  sx={{ minWidth: { sm: 260 } }}
                />
                <Button
                  variant="contained"
                  disableElevation
                  disabled={savingProfilePic}
                  onClick={handleSaveProfilePic}
                  sx={{ ...solidButtonSx, alignSelf: { xs: "stretch", sm: "center" } }}
                >
                  {savingProfilePic ? "Saving…" : "Save"}
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: { xs: "center", md: "flex-end" },
          }}
        >
          <FormControl sx={{ minWidth: { xs: "100%", sm: 200 }, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}>
            <input
              type="text"
              placeholder="Search Collection..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: "16.5px 14px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                fontSize: "1rem",
                width: "100%",
                height: "56px",
                boxSizing: "border-box",
              }}
            />
          </FormControl>

          <FormControl sx={{ minWidth: { xs: "100%", sm: 180 }, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}>
            <InputLabel>Subcategory</InputLabel>
            <Select
              value={filterSubcategory}
              label="Subcategory"
              onChange={(e) => {
                setFilterSubcategory(String(e.target.value));
                setCurrentPage(1);
              }}
              MenuProps={{ disableScrollLock: true }}
            >
              <MenuItem value="all">Subcategory (All)</MenuItem>
              {renderSubcategoryMenuItems(subcategoryTree)}
            </Select>
          </FormControl>

        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Display Preferences
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 220 } }}>
            <InputLabel>Sort Items</InputLabel>
            <Select value={sortItemOption} label="Sort Items" onChange={(e) => handleSortPreferenceChange(e.target.value)} disabled={savingPreference} MenuProps={{ disableScrollLock: true }}>
              {showPrices && <MenuItem value="high">Highest Value</MenuItem>}
              {showPrices && <MenuItem value="low">Lowest Value</MenuItem>}
              <MenuItem value="az">Name A–Z</MenuItem>
              <MenuItem value="za">Name Z–A</MenuItem>
              <MenuItem value="newest">Newest Added</MenuItem>
              <MenuItem value="oldest">Oldest Added</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 220 } }}>
            <InputLabel>Items per page</InputLabel>
            <Select
              value={itemsPerPage}
              label="Items per page"
              onChange={(e) => handleItemsPerPagePreferenceChange(Number(e.target.value))}
              disabled={savingPreference}
              MenuProps={{ disableScrollLock: true }}
            >
              {ALLOWED_ITEMS_PER_PAGE.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            Controls how many items appear on Search and Manage pages.
          </Typography>
        </Box>
        {preferenceMessage && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {preferenceMessage}
          </Alert>
        )}
        {preferenceError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {preferenceError}
          </Alert>
        )}
      </Paper>

      {profileUser && <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Privacy</Typography>
        {!privacyPreferences ? <CircularProgress size={24} aria-label="Loading privacy preferences" /> : (
          <Box sx={{ display: "grid", gap: 2 }}>
            <FormControlLabel sx={{ m: 0, alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}
              labelPlacement="start"
              control={<Switch checked={privacyPreferences.is_private} disabled={Boolean(privacySaving)} onChange={(e) => handlePrivacyToggle("is_private", e.target.checked)} inputProps={{ "aria-label": "Private account" }} />}
              label={<Box><Typography fontWeight={700}>Private account</Typography><Typography variant="body2" color="text.secondary">Only you, accepted friends, and administrators can view your profile, collection, wishlist, and activity.</Typography></Box>}
            />
            <FormControlLabel sx={{ m: 0, alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}
              labelPlacement="start"
              control={<Switch checked={privacyPreferences.show_collection_prices} disabled={Boolean(privacySaving)} onChange={(e) => handlePrivacyToggle("show_collection_prices", e.target.checked)} inputProps={{ "aria-label": "Show collection prices" }} />}
              label={<Box><Typography fontWeight={700}>Show collection prices</Typography><Typography variant="body2" color="text.secondary">Allow other collectors who can view your collection to see item values and totals.</Typography></Box>}
            />
            <FormControlLabel sx={{ m: 0, alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}
              labelPlacement="start"
              control={<Switch checked={privacyPreferences.show_prices_sitewide} disabled={Boolean(privacySaving)} onChange={(e) => handlePrivacyToggle("show_prices_sitewide", e.target.checked)} inputProps={{ "aria-label": "Show prices throughout the website" }} />}
              label={<Box><Typography fontWeight={700}>Show prices throughout the website</Typography><Typography variant="body2" color="text.secondary">Display market prices, collection values, price changes, and price-history information while using The Collector Stop.</Typography></Box>}
            />
          </Box>
        )}
        {privacySaving && <Typography variant="body2" sx={{ mt: 1 }}>Saving…</Typography>}
        {privacyMessage && <Alert severity="success" sx={{ mt: 2 }}>{privacyMessage}</Alert>}
        {privacyError && <Alert severity="error" sx={{ mt: 2 }}>{privacyError}</Alert>}
      </Paper>}

      {/* Subcategory management + bulk actions */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Subcategory panel */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Subcategories
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Create a main subcategory like Pokémon Cards, then add smaller subcategories under it like Graded, Binder, or Sealed.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "260px minmax(220px, 1fr) minmax(260px, 1fr) 88px" },
                gap: 2,
                alignItems: "start",
                mb: 2,
                width: "100%",
              }}
            >
              <Box
              sx={{
                width: "100%",
                p: 1.25,
                borderRadius: 1,
                bgcolor: "action.hover",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="caption" sx={{ display: "block", fontWeight: 700, mb: 0.5 }}>
                Example
              </Typography>
              <Typography variant="body2">Pokémon Cards</Typography>
              <Typography variant="body2" sx={{ pl: 1 }}>
                ↳ Graded Cards
              </Typography>
              <Typography variant="body2" sx={{ pl: 1 }}>
                ↳ Raw Cards
              </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  Subcategory name
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={newSubcategoryName}
                  onChange={(e) => setNewSubcategoryName(e.target.value)}
                />
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  Name the main group or smaller child subcategory.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  Place under
                </Typography>

                <FormControl size="small" fullWidth>
                  <Select
                    displayEmpty
                    value={newSubcategoryParentId}
                    onChange={(e) => setNewSubcategoryParentId(String(e.target.value))}
                    MenuProps={{ disableScrollLock: true }}
                    renderValue={(selected) =>
                      selected ? (
                        topLevelSubcategories.find((s) => String(s.subcategory_id) === String(selected))?.name
                      ) : (
                        <Box component="span" sx={{ color: "text.secondary" }}>
                          None (top-level)
                        </Box>
                      )
                    }
                  >
                    <MenuItem value="">None (top-level)</MenuItem>
                    {topLevelSubcategories.map((parent) => (
                      <MenuItem key={parent.subcategory_id} value={String(parent.subcategory_id)}>
                        {parent.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  Choose Top-level subcategory for a main group. Choose another subcategory to make this a child.
                </Typography>
              </Box>

              <Button
                variant="contained"
                disableElevation
                onClick={handleCreateSubcategory}
                disabled={!newSubcategoryName.trim() || !viewUsername}
                sx={{
                  ...solidButtonCompactSx,
                  height: 40,
                  minWidth: 88,
                  alignSelf: "start",
                  mt: "23px",
                  width: { xs: "100%", md: "auto" },
                }}
              >
                Add
              </Button>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Your subcategory structure
            </Typography>
            {loadingSubs && <Typography variant="body2">Loading…</Typography>}

            <List dense>
              {subcategoryTree.length === 0 && (
                <ListItem>
                  <ListItemText primary="No subcategories yet." />
                </ListItem>
              )}
              {visibleSubcategories.map((sub) => renderSubcategoryRows([sub]))}
            </List>
            {subcategoryTree.length > subcategoriesPerPage && (
              <PaginationControls
                currentPage={subcategoryPage}
                totalPages={subcategoryTotalPages}
                onPageChange={setSubcategoryPage}
                buttonSx={solidButtonCompactSx}
                sx={{ mt: 2 }}
              />
            )}
          </Paper>
        </Grid>

        {/* Bulk item actions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Bulk Actions
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
              }}
            >
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="assign-subcat-label">
                  Assign to Folder
                </InputLabel>
                <Select
                  labelId="assign-subcat-label"
                  label="Assign to Folder"
                  value={assignSubcategory}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAssignSubcategory(
                      v === "all" || v === "" ? "" : String(v)
                    );
                  }}
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {renderSubcategoryMenuItems(subcategoryTree)}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                disableElevation
                disabled={
                  !assignSubcategory ||
                  selectedUserItemIds.length === 0 ||
                  !viewUsername
                }
                onClick={handleAssignSubcategory}
                sx={solidButtonSx}
              >
                Assign Selected
              </Button>

              {showPrices && <Button
                variant="contained"
                disableElevation
                disabled={!hasPriceEdits}
                onClick={handleSavePrices}
                sx={solidButtonSx}
              >
                Save Purchase Prices
              </Button>}

              <Button
                variant="contained"
                disableElevation
                color="error"
                disabled={selectedUserItemIds.length === 0}
                onClick={handleDelete}
                sx={solidButtonSx}
              >
                Delete Selected
              </Button>
            </Box>

            <Typography variant="body2" sx={{ mt: 1 }}>
              Selected items: {selectedUserItemIds.length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Items list */}
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {sorted.length === 0 ? (
        <Typography>No items found for this filter.</Typography>
      ) : (
        <>
          <Box
            sx={{
              mb: 1.5,
              p: 1.25,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
              gap: 1,
              bgcolor: "background.paper",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Checkbox
                checked={allSelectedOnPage(pageItems)}
                indeterminate={
                  selectedUserItemIds.length > 0 &&
                  !allSelectedOnPage(pageItems)
                }
                onChange={() => toggleSelectAllOnPage(pageItems)}
              />
              <Typography variant="body2" fontWeight={700}>
                Select all on this page
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              Selected: {selectedUserItemIds.length}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {pageItems.map((item) => {
              const thumb =
                item.image_url ||
                item.image ||
                item.photo_url ||
                "/logos/default-item.jpg";
              const userItemId = item.user_item_id;
              const purchaseValue =
                editPrices[userItemId] ??
                (item.purchase_price != null
                  ? String(item.purchase_price)
                  : "");
              const isChecked = selectedUserItemIds.includes(userItemId);

              return (
                <Grid item xs={12} md={6} key={userItemId}>
                  <Paper
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: { xs: 1.5, sm: 2 },
                      border: "1px solid",
                      borderColor: isChecked ? "primary.main" : "divider",
                      bgcolor: isChecked
                        ? "rgba(25, 118, 210, 0.06)"
                        : "background.paper",
                      transition: "background-color 120ms ease, border-color 120ms ease",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <Checkbox
                        checked={isChecked}
                        onChange={() => toggleSelect(userItemId)}
                        sx={{ flexShrink: 0 }}
                      />
                      <Avatar
                        variant="rounded"
                        src={toDisplayImageUrl(thumb) || "/logos/default-item.jpg"}
                        sx={{
                          width: { xs: 58, sm: 64 },
                          height: { xs: 58, sm: 64 },
                          flexShrink: 0,
                        }}
                        imgProps={{
                          referrerPolicy: "no-referrer",
                          onError: (e) => {
                            e.currentTarget.src = "/logos/default-item.jpg";
                          },
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            cursor: "pointer",
                            fontWeight: 700,
                            lineHeight: 1.25,
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                          }}
                          onClick={() => navigate(buildItemUrl(item))}
                        >
                          {item.name}
                        </Typography>
                        {showPrices && <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.35,
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                          }}
                        >
                          Value: {formatPrice(item) || "TBD"}
                        </Typography>}
                        {renderSubcategoryChips(item)}
                        <Box sx={{ display: "flex", gap: .5, flexWrap: "wrap", mt: .5 }} aria-label="Ownership statuses">
                          {[['favorite', 'Favorite', 'warning'], ['for_trade', 'For Trade', 'info'], ['for_sale', 'For Sale', 'success'], ['locked', 'Locked', 'default']].filter(([key]) => Number(item[`is_${key}`]) === 1).map(([key, text, color]) => <Chip key={key} size="small" label={text} color={color} variant="outlined" sx={{ height: 22 }} />)}
                        </Box>
                        <Button size="small" variant="outlined" sx={{ mt: .75 }} onClick={(event) => { setOrganizeItem(item); setOrganizeAnchor(event.currentTarget); }} disabled={statusBusy}>Organize</Button>
                        <Menu anchorEl={organizeAnchor} open={Boolean(organizeAnchor) && organizeItem?.user_item_id === item.user_item_id} onClose={() => { setOrganizeAnchor(null); setOrganizeItem(null); }}>
                          {[['favorite', 'Favorite'], ['for_trade', 'For Trade'], ['for_sale', 'For Sale'], ['locked', 'Locked']].map(([key, text]) => <MenuItem key={key} onClick={() => manageStatus(item, key, Number(item[`is_${key}`]) !== 1)}>{Number(item[`is_${key}`]) === 1 ? "✓ " : ""}{text}</MenuItem>)}
                        </Menu>
                      </Box>
                    </Box>

                    <Box sx={{ width: { xs: "100%", sm: 160 }, minWidth: { sm: 140 } }}>
                      {showPrices && <TextField
                        label="Purchase Price"
                        size="small"
                        fullWidth
                        value={purchaseValue}
                        onChange={(e) =>
                          setEditPrices((prev) => ({
                            ...prev,
                            [userItemId]: e.target.value,
                          }))
                        }
                        inputProps={{ inputMode: "decimal" }}
                      />}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {/* Pagination */}
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            buttonSx={solidButtonCompactSx}
          />
        </>
      )}
    </Box>
  );
};

export default Manage;

